/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  CanLeaveDialog,
  ContainerState,
  createDialogRoute,
  getDialogInitData,
  ImmutablePageState,
  queryDirty,
  queryFormMode,
  queryRecalculationNeeded,
  State,
} from '@gh/common-shared';
import { DialogInput } from '@gh/core-ui';
import { FocusedCell } from '@gh/core-ui-grid';
import { Observable } from 'rxjs';
import {
  QuoteDetailsBusinessService,
  QuoteTopDialogConfig,
} from '../../resources/quote-details-business.service';
import { QuoteHousePlanTop } from '../../resources/quote.model';
import { QuoteTopDialogCommandService } from './quote-top-dialog-command.service';
import { QuoteTopDialogGridDefinition } from './quote-top-dialog-grid-definition';
import { quoteTopDialogReducer } from './quote-top-dialog.reducers';
import {
  queryQuoteTops,
  queryQuoteTopsFocusedCell,
  QUOTE_TOP_DIALOG_INIT_STATE,
  QuoteTopDialogState,
} from './quote-top-dialog.state';

export function quoteTopDialogStateFactory(): State<QuoteTopDialogState> {
  return new State(QUOTE_TOP_DIALOG_INIT_STATE, quoteTopDialogReducer);
}

@Component({
  standalone: false,
  selector: 'gh-quote-top-dialog',
  templateUrl: 'quote-top-dialog.component.html',
  styleUrls: ['quote-top-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: State, useFactory: quoteTopDialogStateFactory },
    QuoteTopDialogCommandService,
    QuoteTopDialogGridDefinition,
  ],
})
export class QuoteTopDialogComponent implements OnInit, CanLeaveDialog, DialogInput<QuoteTopDialogConfig> {
  input: QuoteTopDialogConfig;

  tops$: Observable<QuoteHousePlanTop[]>;
  focusedCell$: Observable<Maybe<FocusedCell>>;
  dirty$: Observable<boolean>;
  recalculationNeeded$: Observable<boolean>;
  form: ContainerState<QuoteTopDialogState>;
  housePlanCode: string;

  constructor(@Inject(MAT_DIALOG_DATA) private dialogData: any,
              private dialogRef: MatDialogRef<any>,
              private commands: QuoteTopDialogCommandService,
              public gridDefinition: QuoteTopDialogGridDefinition,
              private state: State<QuoteTopDialogState>) {
  }

  ngOnInit(): void {
    const { tops } = getDialogInitData(this.dialogData);

    this.tops$ = this.state.select(queryQuoteTops);
    this.focusedCell$ = this.state.select(queryQuoteTopsFocusedCell);
    this.recalculationNeeded$ = this.state.select(queryRecalculationNeeded);
    this.dirty$ = this.state.select(queryDirty);

    this.housePlanCode = tops.housePlanCode;
    this.commands.load(this.input.priceBookId, tops, this.input.formMode);

    this.form = new ImmutablePageState(this.state, {
      dirty: queryDirty,
      formMode: queryFormMode,
    });
  }

  onFocusedCellChanged(): void {
    if (this.form.viewMode) {
      return;
    }
    this.commands.resetFocusedCell();
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onProcess(): void {
    const flow = this.commands.processFlow();

    flow.run({ form: this.form, value: this.form.submitValue });
  }

  onRecalculate(): void {
    if (this.form.viewMode) {
      return;
    }

    const flow = this.commands.recalculateFlow();

    flow.run({ form: this.form, value: this.form.submitValue });
  }

  isViewDirty(): boolean {
    if (this.form.viewMode) {
      return false;
    }

    return this.form.dirty;
  }
}

export const quoteTopDialogRoute = createDialogRoute({
  component: QuoteTopDialogComponent,
  init: { service: QuoteDetailsBusinessService, selector: 'initQuoteTopDialog' },
});
